import type { TranslateString } from '@/types/translation.types';
import type { EmailTranslations } from '@/utils/email-translations';
import { buildTranslatedDefaultBlocks } from './default-blocks-i18n';
import type { EmailBlockContentV2 } from './widgets/block-types';
import { WidgetType } from './widgets/widget-registry';

const TEXT_BLOCK_TYPES = new Set<WidgetType>([
  WidgetType.Header,
  WidgetType.Paragraph,
  WidgetType.Footer,
  WidgetType.Button,
]);

// keys on a TranslateString that hold bookkeeping rather than a language's copy
const META_KEYS = new Set(['changed', 'overrides']);

const ENTITIES: Record<string, string> = {
  '&nbsp;': ' ',
  '&amp;': '&',
  '&quot;': '"',
  '&#39;': "'",
  '&apos;': "'",
  '&lt;': '<',
  '&gt;': '>',
};

/** what a reader actually sees - Quill rewrites a plain default into `<p>…</p>` the first time a
 * field is focused, and that markup churn is not the user rewriting the copy */
function readableCopy(html: string | undefined): string {
  return (html ?? '')
    .replace(/<[^>]*>/g, ' ')
    .replace(/&[a-z]+;|&#\d+;/gi, (entity) => ENTITIES[entity.toLowerCase()] ?? entity)
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}

function isTemplateDefaultCopy(stored: string, templateDefault: unknown): boolean {
  return readableCopy(stored) === readableCopy(typeof templateDefault === 'string' ? templateDefault : undefined);
}

/**
 * Per language: the user's own copy wins, anything still sitting on the previous template's
 * default is replaced by the new template's. `changed`/`overrides` ride along with a kept edit so
 * the editor keeps loading it as the user's.
 */
function mergeCopy(
  next: TranslateString,
  stored: TranslateString | undefined,
  previousDefault: TranslateString | undefined
): TranslateString {
  if (!stored) return next;

  const merged: TranslateString = { ...next };
  let keptEdit = false;

  for (const [key, copy] of Object.entries(stored)) {
    if (META_KEYS.has(key) || typeof copy !== 'string') continue;
    // an empty language is copy the campaign never had, not copy the user wrote - carrying it over
    // would blank that language out under the new template
    if (!readableCopy(copy)) continue;
    if (isTemplateDefaultCopy(copy, previousDefault?.[key])) continue;
    merged[key] = copy;
    keptEdit = true;
  }

  if (keptEdit) {
    merged.changed = stored.changed;
    if (stored.overrides) merged.overrides = stored.overrides;
  }

  return merged;
}

/** every language this campaign already carries copy for - its blocks were seeded for all of them
 * at creation, so a template switch has to reseed all of them rather than just the one on screen */
export function campaignBlockLanguages(
  blocks: EmailBlockContentV2[] | undefined,
  baseLanguage: string,
  language: string | undefined
): string[] {
  const languages = new Set([baseLanguage, ...(language ? [language] : [])]);

  for (const block of blocks ?? []) {
    const value = (block.props as { value?: TranslateString }).value;
    if (!value) continue;
    for (const [key, copy] of Object.entries(value)) {
      if (key !== 'base' && !META_KEYS.has(key) && typeof copy === 'string') languages.add(key);
    }
  }

  return Array.from(languages);
}

export interface MergeBlocksForTemplateSwitchArgs {
  nextTemplateId: string;
  previousTemplateId: string | undefined;
  /** the campaign's persisted blocks - every language's copy, not the editor's resolved view */
  previousBlocks: EmailBlockContentV2[] | undefined;
  languages: string[];
  baseLanguage: string;
  translations: EmailTranslations;
  eventTitleFor: (language: string) => string;
}

/**
 * The blocks a template switch should land on: the new template's structure and defaults, with
 * anything the user actually authored carried across. A field still holding the previous
 * template's default copy (or stock image) is filler, not content, so it becomes the new
 * template's - in every language the campaign carries, which is why the defaults come from
 * buildTranslatedDefaultBlocks rather than the english-only getDefaultBlocksForTemplate.
 */
export function mergeBlocksForTemplateSwitch({
  nextTemplateId,
  previousTemplateId,
  previousBlocks,
  languages,
  baseLanguage,
  translations,
  eventTitleFor,
}: MergeBlocksForTemplateSwitchArgs): EmailBlockContentV2[] | undefined {
  const nextBlocks = buildTranslatedDefaultBlocks({
    templateId: nextTemplateId,
    languages,
    baseLanguage,
    translations,
    eventTitleFor,
  });
  if (!nextBlocks || !previousBlocks?.length) return nextBlocks;

  const storedById = new Map(previousBlocks.map((block) => [block.id, block]));
  // an unknown previous template leaves this empty, which reads every stored value as the user's -
  // the safe direction, since it can only keep copy rather than drop it
  const previousDefaults = previousTemplateId
    ? buildTranslatedDefaultBlocks({
        templateId: previousTemplateId,
        languages,
        baseLanguage,
        translations,
        eventTitleFor,
      })
    : undefined;
  const previousDefaultById = new Map((previousDefaults ?? []).map((block) => [block.id, block]));

  return nextBlocks.map((block) => {
    const stored = storedById.get(block.id);
    if (!stored) return block;

    const previousDefault = previousDefaultById.get(block.id);
    const props = { ...block.props };

    if (TEXT_BLOCK_TYPES.has(block.type)) {
      props.value = mergeCopy(
        block.props.value as TranslateString,
        stored.props.value as TranslateString | undefined,
        previousDefault?.props.value as TranslateString | undefined
      );
    }

    // an image the user uploaded, as opposed to the previous template's stock art
    const storedSrc = stored.props.src;
    if (typeof storedSrc === 'string' && storedSrc && storedSrc !== (previousDefault?.props.src ?? '')) {
      props.src = storedSrc;
    }

    return { ...block, props };
  });
}
